import { Body, Tooltip } from '@assembly-js/design-system';
import type { IHeaderParams } from 'ag-grid-community';
import React, { useState } from 'react';

import { ArrowDownIcon, QuestionIcon } from 'src/components/icons';

interface HeaderRenderProps extends IHeaderParams {
  tooltipDescription?: (props: { isClient?: boolean }) => React.ReactNode;
  menuIcon: string;
  isClient?: boolean;
}

type SortDirection = 'asc' | 'desc' | null;

const readSortDirection = (column: IHeaderParams['column']): SortDirection => {
  const sort = column.getSort();
  if (sort === 'asc' || sort === 'desc') return sort;
  return null;
};

const HeaderRender: React.FC<HeaderRenderProps> = (props) => {
  const {
    column,
    displayName,
    enableSorting,
    enableMenu,
    setSort,
    showColumnMenu,
    tooltipDescription,
    menuIcon,
    isClient,
  } = props;

  const [sortDirection, setSortDirection] = useState<SortDirection>(() =>
    readSortDirection(column),
  );
  const [menuRef, setMenuRef] = useState<null | HTMLElement>();

  React.useEffect(() => {
    const onSortChanged = () => setSortDirection(readSortDirection(column));
    column.addEventListener('sortChanged', onSortChanged);
    onSortChanged();
    return () => {
      column.removeEventListener('sortChanged', onSortChanged);
    };
  }, [column]);

  const onMenuClicked = () => {
    if (menuRef) {
      showColumnMenu(menuRef);
    }
  };

  const menuContent = (
    <div
      ref={(menuButton) => {
        setMenuRef(menuButton);
      }}
      className="customHeaderMenuButton"
      onClick={onMenuClicked}
      onKeyDown={onMenuClicked}
      role="button"
      tabIndex={0}
    >
      <i className={`fa ${menuIcon}`} />
    </div>
  );

  const sortContent = (
    <div className="mr-1">
      {sortDirection === 'asc' && (
        <ArrowDownIcon className="rotate-180 text-[11px] text-black-heading" />
      )}
      {sortDirection === 'desc' && (
        <ArrowDownIcon className="text-[11px] text-black-heading" />
      )}
    </div>
  );

  const handleSort = (event: React.MouseEvent<HTMLElement>) => {
    if (sortDirection === 'asc') {
      setSort('desc', event.shiftKey);
      return;
    }
    if (sortDirection === 'desc') {
      setSort(null, event.shiftKey);
      return;
    }
    setSort('asc', event.shiftKey);
  };

  const tooltipContent = tooltipDescription && tooltipDescription({ isClient });

  return (
    <div onClick={handleSort} className="flex items-center">
      {enableMenu && menuContent}
      <Body
        tag="div"
        size="sm"
        className="mr-1 text-[rgba(17,51,83,0.7)] leading-5"
      >
        {displayName}
      </Body>
      {enableSorting && sortContent}
      {tooltipContent ? (
        <Tooltip position="bottom" content={tooltipContent}>
          <div>
            <QuestionIcon className="text-[13px] text-gray-small [&_path]:[stroke-width:1]" />
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default HeaderRender;
